import { useQuery } from '@tanstack/react-query'
import { AddressLink } from 'Modules'
import { erc20Abi, formatUnits, parseAbi } from 'viem'

import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

/** RedemptionPool views (ASSET_SALE.md "Views") - PTO ERC-3643 pools only */
const REDEMPTION_POOL_ABI = parseAbi([
  'function poolInfos() view returns (address pto, uint256 totalPTOs, uint256 amountPerPTO, uint256 startTime, uint256 deadline, bool isEnded, uint8 poolType)',
  'function totalPTOClaimed() view returns (uint256)',
  'function rewardToken() view returns (address)',
])

export interface RedemptionPoolFunding {
  tokenAddress: `0x${string}`
  symbol: string
  decimals: number
  /** reward token the pool holds now */
  balance: bigint
  /** still owed to holders: (totalPTOs - totalPTOClaimed) x amountPerPTO */
  needed: bigint
  /** needed - balance, floored at 0 */
  shortfall: bigint
  isEnded: boolean
}

const fetchRedemptionPoolFunding = async (
  poolAddress: `0x${string}`
): Promise<RedemptionPoolFunding> => {
  const pool = { address: poolAddress, abi: REDEMPTION_POOL_ABI } as const
  const [infos, claimed, tokenAddress] = await Promise.all([
    publicWallet.readContract({ ...pool, functionName: 'poolInfos' }),
    publicWallet.readContract({ ...pool, functionName: 'totalPTOClaimed' }),
    publicWallet.readContract({ ...pool, functionName: 'rewardToken' }),
  ])
  const [, totalPTOs, amountPerPTO, , , isEnded] = infos

  const token = { address: tokenAddress, abi: erc20Abi } as const
  const [balance, decimals, symbol] = await Promise.all([
    publicWallet.readContract({ ...token, functionName: 'balanceOf', args: [poolAddress] }),
    publicWallet.readContract({ ...token, functionName: 'decimals' }),
    publicWallet.readContract({ ...token, functionName: 'symbol' }),
  ])

  const needed = (totalPTOs - claimed) * amountPerPTO
  return {
    tokenAddress,
    symbol,
    decimals,
    balance,
    needed,
    shortfall: needed > balance ? needed - balance : 0n,
    isEnded,
  }
}

/**
 * PTO ERC-3643 pools are not funded on creation (DAO_GOVERNANCE_ERC3643_FE_NOTE 6.3):
 * claims revert until the pool holds the reward token. Reads what the pool still
 * owes vs what it holds, and tops it up from the connected wallet.
 */
export const useRedemptionPoolFunding = (poolAddress?: string | null, enabled = true) => {
  const query = useQuery({
    queryKey: ['daoVoting', 'redemptionPoolFunding', poolAddress],
    queryFn: () => fetchRedemptionPoolFunding(poolAddress as `0x${string}`),
    enabled: enabled && !!poolAddress,
    refetchOnWindowFocus: false,
  })

  /** ERC20.transfer(pool, shortfall) from the connected wallet */
  const fundPool = async (address: AddressLink, funding: RedemptionPoolFunding) => {
    if (!address) throw new Error('Wallet is not connected')
    if (!poolAddress) throw new Error('Pool contract address is missing')

    const account = address as `0x${string}`
    const walletBalance = await publicWallet.readContract({
      address: funding.tokenAddress,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [account],
    })
    if (walletBalance < funding.shortfall) {
      throw new Error(
        `The connected wallet has ${formatUnits(walletBalance, funding.decimals)} ${
          funding.symbol
        }, but ${formatUnits(funding.shortfall, funding.decimals)} ${funding.symbol} is needed.`
      )
    }

    try {
      const { request } = await publicWallet.simulateContract({
        account,
        address: funding.tokenAddress,
        abi: erc20Abi,
        functionName: 'transfer',
        args: [poolAddress as `0x${string}`, funding.shortfall],
      })
      const txHash = await clientWallet.writeContract(request)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 6,
      })
      if (receipt.status === 'reverted') throw new Error('Transaction reverted on-chain')

      return { txHash, receipt }
    } catch (error) {
      logError('error fund redemption pool on-chain-----------------', error)
      throw error
    }
  }

  return {
    funding: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fundPool,
  }
}
